// eslint-disable-next-line @altermaind/you-shall-not-pass
import { FC, useState, useEffect, useCallback, ChangeEvent, useMemo, DragEvent } from 'react';
import { Controller, FieldValues } from 'react-hook-form';
import { useTranslations } from '@altermaind/core/i18n';
import { Asset, Box, Button, Card, Input, Svg, Typography } from '@altermaind/web/ui';
import { convertBytesToReadableSize } from '@/_shared/helpers/convertBytesToReadableSize';
import { FileTypes } from '@/_shared/types/fileTypes';
import { AlertBadge } from '../../AlertBadge';
import { Tooltip } from '../../Tooltip';
import { Props, UploadedFile } from './types';

const FILETYPE_MAP: Record<
  `${FileTypes}`,
  { mimes?: string[]; exts?: string[]; startsWithMime?: string[] }
> = {
  [FileTypes.ALL_IMAGES]: {
    startsWithMime: ['image/'],
    exts: ['.png', '.jpeg', '.jpg', '.gif', '.webp', '.svg'],
  },
  [FileTypes.PNG_IMAGES]: { mimes: ['image/png'], exts: ['.png'] },
  [FileTypes.JPEG_IMAGES]: { mimes: ['image/jpeg'], exts: ['.jpeg', '.jpg'] },
  [FileTypes.PDF]: { mimes: ['application/pdf'], exts: ['.pdf'] },
  [FileTypes.EXCEL]: { mimes: ['application/vnd.ms-excel'], exts: ['.xls'] },
  [FileTypes.EXCEL_XLSX]: {
    mimes: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
    exts: ['.xlsx'],
  },
  [FileTypes.CSV]: { mimes: ['text/csv', 'application/csv'], exts: ['.csv'] },
  [FileTypes.SVG]: { mimes: ['image/svg+xml'], exts: ['.svg'] },
  [FileTypes.TEXT]: { mimes: ['text/plain'], exts: ['.txt'] },
  [FileTypes.WORD]: { mimes: ['application/msword'], exts: ['.doc'] },
  [FileTypes.WORD_DOCX]: {
    mimes: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    exts: ['.docx'],
  },
};

const convertFileTypesToExtensions = (types: `${FileTypes}`[]): string => {
  const EXTENSIONS_MAP: Record<`${FileTypes}`, string[]> = (
    Object.keys(FILETYPE_MAP) as `${FileTypes}`[]
  ).reduce(
    (acc, key) => {
      acc[key] = FILETYPE_MAP[key]?.exts ?? [];
      return acc;
    },
    {} as Record<`${FileTypes}`, string[]>,
  );

  const extensions = types.flatMap(type => EXTENSIONS_MAP[type] ?? []);
  const unique = Array.from(new Set(extensions));

  return unique.join(', ');
};

const isFileTypeAllowed = (file: File, supported: `${FileTypes}`[]) => {
  const name = file.name.toLowerCase();
  const mime = file.type; // may be an empty string for drag & drop from older file systems
  return supported.some(ft => {
    const cfg = FILETYPE_MAP[ft];
    const okStartsWith = cfg.startsWithMime?.some(prefix => mime.startsWith(prefix)) ?? false;
    const okMime = cfg.mimes?.includes(mime) ?? false;
    const okExt = cfg.exts?.some(ext => name.endsWith(ext)) ?? false;
    return okStartsWith || okMime || okExt;
  });
};

/**
 * FileInput Component
 *
 * This component provides a file input field with enhanced functionality, including file validation, preview, and support for various file types. It is built using React and integrates with `react-hook-form` for form handling.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {Object} props.control - The `control` object from `react-hook-form` to manage form state.
 * @param {string} props.name - The name of the input field, used for form registration and validation.
 * @param {Object} [props.additonalProps] - Additional validation rules or properties for the input field.
 * @param {Array<string>} [props.supportedFileTypes=[FileTypes.ALL_IMAGES]] - Array of supported file types, defaulting to all image formats.
 * @param {string} [props.tooltip] - Tooltip text to display next to the label.
 * @param {Object} [props.tooltipProps={}] - Additional properties for the tooltip component.
 * @param {Function} [props.trigger] - Function to trigger validation manually.
 * @param {number} [props.MAX_SIZE_IN_MB=0.5] - Maximum file size allowed in megabytes, defaulting to 0.5 MB.
 * @param {Object} [props.value] - Initial value for the input field, typically a base64 string.
 * @param {string} [props.label] - Label text for the input field.
 * @param {string} [props.placeholder] - Placeholder text displayed when no file is uploaded.
 * @param {string} [props.maxFileSizeDescription] - Custom description for the maximum file size.
 * @param {string} [props.supportFilesDescription] - Custom description for supported file formats.
 *
 * @returns {JSX.Element} The rendered FileInput component.
 *
 * ### Example Usage:
 * @example
 * ```jsx
 * import { useForm, Controller } from 'react-hook-form';
 * import { FileInput } from './FileInput';
 * import { FileTypes } from '@/_shared/types';
 *
 * const MyForm = () => {
 *   const { control, handleSubmit } = useForm();
 *
 *   const onSubmit = (data) => {
 *     console.log(data);
 *   };
 *
 *   return (
 *     <form onSubmit={handleSubmit(onSubmit)}>
 *       <FileInput
 *         control={control}
 *         name="profilePicture"
 *         supportedFileTypes={[FileTypes.PNG_IMAGES, FileTypes.JPEG_IMAGES]}
 *         MAX_SIZE_IN_MB={2}
 *         label="Upload Profile Picture"
 *         placeholder="Choose a file"
 *       />
 *       <button type="submit">Submit</button>
 *     </form>
 *   );
 * };
 * ```
 */
export const FileInput: FC<Props> = ({
  control,
  name,
  additonalProps,
  supportedFileTypes = [FileTypes.ALL_IMAGES],
  tooltip,
  tooltipProps = {},
  trigger,
  MAX_SIZE_IN_MB = 0.5,
  hasError,
  ...props
}) => {
  const t = useTranslations('inputs.fileInput');
  // eslint-disable-next-line @altermaind/no-magic-numbers
  const MAX_BYTES = MAX_SIZE_IN_MB * 1024 * 1024;
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | undefined>(undefined);
  const [isDragging, setIsDragging] = useState(false);

  const onUploadFile = useCallback((e: ChangeEvent<HTMLInputElement>, field: FieldValues) => {
    const userUploadedFile = e.target.files?.[0];
    if (userUploadedFile) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        setUploadedFile({
          file: userUploadedFile,
          base64,
        });
        field.onChange({ value: base64, file: userUploadedFile });
      };
      reader.readAsDataURL(userUploadedFile);
    }
  }, []);

  const onRemoveFile = useCallback(
    (field: FieldValues) => {
      setUploadedFile(undefined);
      const input = document.getElementById(name) as HTMLInputElement;
      if (input) {
        input.value = '';
      }
      field.onChange(undefined);
    },
    [name],
  );

  const cardBorderColor = useMemo(() => {
    if (hasError) return 'error';
    if (isDragging) return 'primary';
    if (uploadedFile) return 'primary';
    return 'grey/light';
  }, [hasError, uploadedFile, isDragging]);

  const forwardFilesToHiddenInput = useCallback(
    (file: File): boolean => {
      const input = document.getElementById(name) as HTMLInputElement | null;
      if (!input) return false;

      const dt = new DataTransfer();
      dt.items.add(file);
      (input as HTMLInputElement).files = dt.files;

      input.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    },
    [name],
  );

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragEnter = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    async (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = e.dataTransfer?.files;
      if (!files || files.length === 0) return;

      const file = files[0];

      const pushed = forwardFilesToHiddenInput(file);
      if (!pushed) {
        return;
      }

      const isValid = await trigger?.(name);
      if (!isValid) {
        const input = document.getElementById(name) as HTMLInputElement | null;
        if (input) input.value = '';
        setUploadedFile(undefined);
      }
    },
    [forwardFilesToHiddenInput, trigger, name],
  );

  useEffect(() => {
    if (props.value && props.value.startsWith('data:')) {
      const isImage = props.value.startsWith('data:image/');
      // Create a "placeholder" File only to have name/type in the UI
      const mime =
        props.value.substring(5, props.value.indexOf(';')) ||
        (isImage ? 'image/*' : 'application/octet-stream');

      setUploadedFile({
        file: new File([], 'file', { type: mime }),
        base64: props.value,
      });
    }

    return () => {
      setUploadedFile(undefined);
    };
  }, [props.value]);

  return (
    <Controller
      control={control}
      name={name}
      rules={{
        validate: {
          size: (files?: FileList) =>
            !files?.[0] || files?.[0]?.size <= MAX_BYTES ? true : t('errors.maxFileSize'),
          type: (files?: FileList) =>
            !files?.[0] || isFileTypeAllowed(files[0], supportedFileTypes)
              ? true
              : t('errors.typeNotAllowed'),
        },
        ...additonalProps,
      }}
      render={({ field, fieldState }) => {
        return (
          <Box width='100%'>
            <Box
              display='flex'
              flexDirection='row'
              alignItems='center'
              justifyContent='space-between'
              mb='xs'
            >
              <Typography variant='h3'>
                {props.label}
                {tooltip && (
                  <Tooltip
                    iconProps={{ size: 'xs', fill: 'grey/light' }}
                    containerProps={{ ml: 'xxs' }}
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...tooltipProps}
                  >
                    {tooltip}
                  </Tooltip>
                )}
              </Typography>
              {uploadedFile?.file && uploadedFile?.file.type.startsWith('image/') && (
                <Button
                  onClick={() => onRemoveFile(field)}
                  minWidth='none'
                  px='xxs'
                  height='m'
                  py='xxs'
                  variant='primary'
                >
                  <Svg name='trash' size='xs' />
                </Button>
              )}
            </Box>
            <Input
              id={name}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...field}
              value={undefined}
              display='none'
              onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                const files = e.target.files ?? undefined;
                field.onChange(files);
                const isFileValid = await trigger(name); // force file validation
                if (isFileValid) {
                  onUploadFile(e, field);
                } else {
                  onRemoveFile(field);
                }
              }}
              type='file'
              accept={supportedFileTypes.join(',')}
              multiple={false}
            />
            <Card
              style={{ cursor: 'pointer' }}
              onClick={() => {
                const input = document.getElementById(name) as HTMLInputElement;
                input?.click();
              }}
              border={uploadedFile ? '1px solid' : '1px dashed'}
              borderColor={cardBorderColor}
              width='100%'
              textAlign='center'
              minHeight='auto'
            >
              {uploadedFile ? (
                <Box>
                  {uploadedFile?.file.type.startsWith('image/') ? (
                    <Asset
                      data-testid={`file-${name}`}
                      domain='images'
                      src={uploadedFile.base64}
                      width='150'
                    />
                  ) : (
                    <Box
                      display='flex'
                      flexDirection='row'
                      justifyContent='space-between'
                      alignItems='center'
                    >
                      <Svg name='document' size='l' />
                      <Box ml='xs' textAlign='left' flex={1}>
                        <Typography variant='p2/light' mb='none'>
                          {uploadedFile?.file.name}
                        </Typography>
                        <Typography variant='p2/light' fontSize='xxs' mt='none'>
                          {convertBytesToReadableSize(uploadedFile?.file.size || 0)}
                        </Typography>
                      </Box>
                      <Button
                        onClick={e => {
                          onRemoveFile(field);
                          e.stopPropagation();
                        }}
                        minWidth='none'
                        px='xxs'
                        height='m'
                        py='xxs'
                        variant='primary'
                        bg='transparent'
                      >
                        <Svg name='trash' size='s' />
                      </Button>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box
                  onDragOver={handleDragOver}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <Svg name='documentPlus' size='m' />
                  <Typography variant='p2/light' color='grey/light' mt='xs'>
                    {props.placeholder || t('placeholder')}
                  </Typography>
                  <Typography variant='p2/light' color='grey/light' mt='xxxxs'>
                    {props?.maxFileSizeDescription ||
                      t('maxFileSizeDescription', {
                        size: convertBytesToReadableSize(MAX_BYTES),
                      })}
                  </Typography>
                  <Typography variant='p2/light' color='grey/light' mt='xxxxs'>
                    {props?.supportFilesDescription ||
                      t('supportFilesDescription', {
                        formats: convertFileTypesToExtensions(supportedFileTypes),
                      })}
                  </Typography>
                </Box>
              )}
            </Card>
            {fieldState.error?.message && (
              <Box mt='xs'>
                <AlertBadge iconName='info' type='error' label={fieldState.error.message} />
              </Box>
            )}
          </Box>
        );
      }}
    />
  );
};
